
import { useSnackbar } from '@components/feedback/GlobalSnackbar';
import { useApiError } from '@hooks/useApiError';
import { EditOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { useAuthStore } from '@store/authStore';
import { useRef, useState } from 'react';

import { CANCEL_BTN, CARD, FIELD, FIELD_DISABLED, LABEL, PENCIL_BTN, SAVE_BTN, TITLE } from '../styles';
import { usePatchMe } from '../useSettingsData';

export const UserInfoCard = () => {
  const user    = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const { show: showSnack } = useSnackbar();
  const patchMe = usePatchMe();

  const [isEditingUserInfo, setIsEditingUserInfo] = useState(false);
  const [fullName,     setFullName]     = useState(user?.name ?? '');
  const [isSavingName, setIsSavingName] = useState(false);
  const prevFullNameRef = useRef(user?.name ?? '');

  return (
    <Box sx={CARD}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '20px' }}>
        <Box>
          <Typography sx={TITLE}>User Information</Typography>
          <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.grey[500], mt: '4px' })}>
            Update user information and permissions.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => { prevFullNameRef.current = fullName; setIsEditingUserInfo(true); }}
          sx={(theme) => ({ ...PENCIL_BTN, ...(isEditingUserInfo ? { color: theme.palette.primary.main, bgcolor: theme.palette.status.info.bg } : {}) })}
        >
          <EditOutlined sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '16px', mb: '20px' }}>
        <Box>
          <Typography component="label" sx={LABEL}>Full Name</Typography>
          <TextField
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={!isEditingUserInfo}
            fullWidth
            sx={isEditingUserInfo ? FIELD : FIELD_DISABLED}
            slotProps={{ htmlInput: { style: { paddingLeft: 12, paddingRight: 12 } } }}
          />
        </Box>
        <Box>
          <Typography component="label" sx={LABEL}>Role</Typography>
          <TextField
            value={user?.role ?? ''}
            disabled
            fullWidth
            sx={{
              ...FIELD_DISABLED,
              '& .MuiOutlinedInput-input': { textTransform: 'capitalize' },
            }}
            slotProps={{ htmlInput: { style: { paddingLeft: 12, paddingRight: 12 } } }}
          />
        </Box>
      </Box>

      {isEditingUserInfo && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button
            variant="outlined"
            sx={CANCEL_BTN}
            onClick={() => { setFullName(prevFullNameRef.current); setIsEditingUserInfo(false); }}
          >
            Cancel
          </Button>
          <Button
            sx={SAVE_BTN}
            disabled={isSavingName}
            onClick={async () => {
              const name = fullName.trim();
              if (!name) return;
              setIsSavingName(true);
              try {
                const { data } = await patchMe.mutateAsync(name);
                if (user) setUser({ ...user, name: data.user.name });
                setFullName(data.user.name);
                setIsEditingUserInfo(false);
                showSnack('Name updated successfully.', 'success');
              } catch (err) {
                showSnack(useApiError(err) ?? 'Failed to update name.', 'error');
              } finally {
                setIsSavingName(false);
              }
            }}
          >
            {isSavingName ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
